import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { GridDataResult, PageChangeEvent } from '@progress/kendo-angular-grid';
import { SortDescriptor } from '@progress/kendo-data-query';
import { finalize } from 'rxjs/operators';
import * as fileSaver from 'file-saver';
import { SnackbarService } from '@app/core/services/snackbar.service';
import { OverlayService } from '@app/shared/components/overlay/overlay.service';
import { ConfirmDialogComponent } from '@app/shared/components/confirm-dialog/confirm-dialog.component';
import { FormGeneratorDialogComponent } from '@app/shared/components/form-generator-dialog/form-generator-dialog.component';
import { Filterable } from '@app/shared/components/filter/filterable';
import { RowActionButtonInput, RowActionEventOutput } from '@app/modules/talent-track/talent-track-edit-employee/edit-employee/components/employment-records/components/employment-record-details/components/data-grid-comp/models/data-grid-comp.model';
import { CultureService } from '@app/core/services/system-language/culture.service';
import { Culture } from '@app/shared/models/system-language/culture.model';
import { ReportDesignerService } from '../../services/report-designer.service';
import { Report, ReportStatus, ReportType } from '../../models/report-designer.model';
import { AssignReportSecurityDialogComponent } from '../assign-report-security-dialog/assign-report-security-dialog.component';
import { ChangeReportStatusDialogComponent } from '../change-report-status-dialog/change-report-status-dialog.component';
import { EditReportDialogComponent, EditReportDialogResult } from '../edit-report-dialog/edit-report-dialog.component';

@Component({
  selector: 'app-reports-admin',
  templateUrl: './reports-admin.component.html',
  styleUrls: ['./reports-admin.component.scss']
})
export class ReportsAdminComponent implements OnInit, Filterable {
  public gridDataResult: GridDataResult;
  public isLoading: boolean;
  public pageSize: number = 20;
  public createReportFormId: string = 'frm_Rd1nAeRzRzEDRI';
  public skip: number = 0;
  private sortString = 'version.createdOn-desc';
  public filterString: string;

  public sort: SortDescriptor[] = [
    { field: 'version', dir: 'desc' }
  ];

  public columns: any[] = [
    { field: 'name', title: 'Name', dataType: 'String' },
    { field: 'description', title: 'Description', type: 'longText', dataType: 'String' },
    { field: 'type', subField: 'name', title: 'Report Type', dataType: 'String' },
    { field: 'status', subField: 'name', title: 'Status', type: 'chip', chipValue: ['Draft', 'Published', 'Archived'], dataType: 'String' },
    { field: 'reportDataSource', subField: 'name', title: 'Data Source', dataType: 'String' },
    { field: 'culture', subField: 'text', title: 'Culture', dataType: 'String' },
    { field: 'version', subField: 'createdOn', title: 'Created On', type: 'date', dataType: 'Date' },
  ];

  public sortableColumns: any[] = [
    { field: 'name', sortValue: 'name' },
    { field: 'description', sortValue: 'description' },
    { field: 'type', sortValue: 'type.name' },
    { field: 'status', sortValue: 'status.name' },
    { field: 'reportDataSource', sortValue: 'reportDataSource.name' },
    { field: 'culture', sortValue: 'culture.text' },
    { field: 'version', sortValue: 'version.createdOn' },
  ];

  public filterCategories: any[] = [
    { field: 'name', title: 'Name', dataType: 'String' },
    { field: 'description', title: 'Description', dataType: 'String' },
    { field: 'type.name', title: 'Report Type', dataType: 'String' },
    {
      field: 'status.id', title: 'Status', dataType: 'ProvidedList',
      listOptions: { list: [], id: 'id', name: 'name' }
    },
    { field: 'reportDataSource.name', title: 'Data Source', dataType: 'String' },
    { field: 'culture.text', title: 'Culture', dataType: 'String' },
    { field: 'version.createdOn', title: 'Created On', dataType: 'Date' },
  ];

  public rowActionButtons: RowActionButtonInput[] = [
    { id: 'edit', name: 'Edit Details', icon: 'edit' },
    // { id: 'change-status', name: 'Change Status', icon: 'published_with_changes' },
    // { id: 'assign-security', name: 'Assign Security', icon: 'security' },
    { id: 'download', name: 'Download', icon: 'file_download' },
    { id: 'delete', name: 'Delete', icon: 'delete' },
  ];

  private getRequest: any;
  private reportStatuses: ReportStatus[] = [];
  private reportTypes: ReportType[] = [];
  private cultures: Culture[] = [];
  private createReportDialogRef: MatDialogRef<FormGeneratorDialogComponent>;

  constructor(
    private router: Router,
    private dialog: MatDialog,
    private snackbarService: SnackbarService,
    private overlayService: OverlayService,
    private reportDesignerService: ReportDesignerService,
    private cultureService: CultureService,
  ) { }

  ngOnInit(): void {
    this.loadReportStatuses();
    this.loadReportTypes();
    this.loadCultures();
    this.getReports();
  }

  private loadReportStatuses(): void {
    this.reportDesignerService.getReportStatuses().subscribe(pagedStatuses => {
      this.reportStatuses = pagedStatuses.data;
      const statusFilter = this.filterCategories.find(category => category.field === 'status.id');
      if (statusFilter) {
        statusFilter.listOptions.list = this.reportStatuses;
      }
    });
  }

  private loadReportTypes(): void {
    this.reportDesignerService.getReportTypes().subscribe(pagedTypes => {
      this.reportTypes = pagedTypes.data;
    });
  }

  private loadCultures(): void {
    this.cultureService.getCultures().subscribe(cultures => {
      this.cultures = cultures || [];

      // getReports() can resolve before this call does - re-apply to whatever's already loaded.
      this.gridDataResult?.data?.forEach(report => this.applyCultureDisplayName(report));
    });
  }

  getReports(): void {
    this.isLoading = true;

    if (this.getRequest) {
      this.getRequest.unsubscribe();
    }

    this.getRequest = this.reportDesignerService.getReports(this.pageSize, this.skip, this.sortString, this.filterString)
      .pipe(
        finalize(() => this.isLoading = false)
      )
      .subscribe(
        pagedReports => {
          pagedReports.data.forEach(report => this.applyCultureDisplayName(report));

          this.gridDataResult = {
            data: pagedReports.data,
            total: pagedReports.total,
          };
        }
      );
  }

  // The API returns Culture.text as the raw culture code (e.g. "en-US") since resolving a
  // friendly display name isn't translatable to SQL - the cultures lookup already fetched for
  // the edit dialog has that display name, so swap it in for the grid.
  private applyCultureDisplayName(report: Report): void {
    if (!report.culture?.id) {
      return;
    }

    const match = this.cultures.find(c => c.id === report.culture.id);
    if (match) {
      report.culture.text = match.text;
    }
  }

  onOpenDialog(report?: Report): void {
    if (report?.id) {
      this.router.navigate(['/Reports/Designer', report.id]);
    } else {
      this.openCreateReportDialog();
    }
  }

  private openCreateReportDialog(): void {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.data = {
      formId: this.createReportFormId,
      formData: null,
    };

    this.createReportDialogRef = this.dialog.open(FormGeneratorDialogComponent, dialogConfig);

    const sub = this.createReportDialogRef.componentInstance.emitFormData.subscribe(formData => {
      this.createReport(formData);
    });

    this.createReportDialogRef.afterClosed().subscribe(() => sub.unsubscribe());
  }

  private createReport(formData: any): void {
    const cultureId = this.toId(formData.cultureId) ?? 'en-US';

    const payload = {
      // The name field may be a localization field (array of { culture, text }) or a plain string.
      name: Array.isArray(formData.name)
        ? formData.name
        : [{ culture: cultureId, text: formData.name }],
      description: formData.description || null,
      reportTypeId: this.toId(formData.reportTypeId),
      reportDataSourceId: this.toId(formData.reportDataSourceId),
      cultureId: cultureId,
    };

    this.overlayService.show();

    this.reportDesignerService.createReport(payload)
      .pipe(finalize(() => this.overlayService.hide()))
      .subscribe({
        next: (report) => {
          this.createReportDialogRef?.close();

          const reportId = report?.id || report?.reportId;
          if (reportId) {
            this.router.navigate(['/Reports/Designer', reportId]);
            return;
          }

          this.snackbarService.openSnackBar('Report created successfully', 'clear', 'success');
          this.getReports();
        },
        error: () => {
          this.snackbarService.openSnackBar('Unable to create report', 'clear', 'warn');
        }
      });
  }

  // Form generator dropdowns can emit either the raw id or the selected { id, name } object.
  private toId(value: any): string {
    return value && typeof value === 'object' ? value.id : value;
  }

  goToReports(): void {
    this.router.navigate(['/Reports']);
  }

  search(searchValue: string): void {
    this.filterString = searchValue ? `(Name like "${searchValue}")` : null;
    this.skip = 0;
    this.getReports();
  }

  filterCallback(filterString: string): void {
    this.filterString = filterString;
    this.skip = 0;
    this.getReports();
  }

  pageChange(event: PageChangeEvent): void {
    this.skip = event.skip;
    this.pageSize = event.take;
    this.getReports();
  }

  onSort(sortString: string): void {
    this.sortString = sortString || 'version.createdOn-desc';
    this.getReports();
  }

  rowAction(event: RowActionEventOutput): void {
    switch (event.id) {
      case 'edit':
        this.openEditDialog(event.rowData);
        break;
      case 'delete':
        this.confirmDeleteReport(event.rowData);
        break;
      case 'assign-security':
        this.openAssignSecurityDialog(event.rowData);
        break;
      case 'change-status':
        this.openChangeStatusDialog(event.rowData);
        break;
      case 'download':
        this.downloadReport(event.rowData);
        break;
    }
  }

  private openEditDialog(report: Report): void {
    this.overlayService.show();

    this.reportDesignerService.getReport(report.id)
      .pipe(finalize(() => this.overlayService.hide()))
      .subscribe({
        next: (fullReport: any) => {
          const dialogConfig = new MatDialogConfig();
          dialogConfig.disableClose = true;
          dialogConfig.autoFocus = true;
          dialogConfig.data = {
            reportName: report.name,
            name: report.name,
            description: fullReport.description ?? '',
            reportTypeId: fullReport.type?.id,
            reportTypes: this.reportTypes,
            cultureId: fullReport.culture?.id,
            cultures: this.cultures,
          };

          const dialogRef = this.dialog.open(EditReportDialogComponent, dialogConfig);
          dialogRef.afterClosed().subscribe((result: EditReportDialogResult) => {
            if (!result) {
              return;
            }

            this.saveReportEdit(report, fullReport, result);
          });
        },
        error: () => {
          this.snackbarService.openSnackBar('Unable to load report', 'clear', 'warn');
        }
      });
  }

  private saveReportEdit(report: Report, fullReport: any, result: EditReportDialogResult): void {
    const payload = {
      name: this.buildNameLocalizations(fullReport, report.name, result.name),
      description: result.description,
      reportTypeId: result.reportTypeId,
      reportDataSourceId: fullReport.reportDataSource?.id,
      cultureId: result.cultureId,
    };

    this.overlayService.show();

    this.reportDesignerService.updateReport(report.id, payload)
      .pipe(finalize(() => this.overlayService.hide()))
      .subscribe({
        next: () => {
          this.snackbarService.openSnackBar('Report updated successfully', 'clear', 'success');
          this.getReports();
        },
        error: () => {
          this.snackbarService.openSnackBar('Unable to update report', 'clear', 'warn');
        }
      });
  }

  // The list/detail endpoints resolve Name to a single string in the viewer's culture, but the
  // full record can hold a localization per culture - round-tripping only the edited entry (found
  // by matching the original displayed text) keeps any other cultures' names intact.
  private buildNameLocalizations(fullReport: any, originalText: string, newText: string): { culture: string, text: string }[] {
    const existing: { culture: string, text: string }[] = Array.isArray(fullReport.name) ? fullReport.name : [];
    const matchedCulture = existing.find(n => n.text === originalText)?.culture
      ?? fullReport.culture?.id
      ?? existing[0]?.culture
      ?? 'en-US';

    const others = existing.filter(n => n.culture !== matchedCulture);
    return [...others, { culture: matchedCulture, text: newText }];
  }

  private confirmDeleteReport(report: Report): void {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.data = {
      text: `Are you sure you want to delete "${report.name}"?`
    };

    const dialogRef = this.dialog.open(ConfirmDialogComponent, dialogConfig);
    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed === true) {
        this.overlayService.show();

        this.reportDesignerService.deleteReport(report.id)
          .pipe(finalize(() => this.overlayService.hide()))
          .subscribe(
            () => {
              this.getReports();
              this.snackbarService.openSnackBar('Report deleted successfully', 'clear', 'success');
            },
            err => {
              this.snackbarService.openSnackBar('Unable to delete report', 'clear', 'warn');
            }
          );
      }
    });
  }

  private openAssignSecurityDialog(report: Report): void {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.data = {
      reportId: report.id,
      reportName: report.name
    };

    this.dialog.open(AssignReportSecurityDialogComponent, dialogConfig);
  }

  private openChangeStatusDialog(report: Report): void {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.data = {
      reportId: report.id,
      reportName: report.name,
      currentStatusId: report.status?.id,
      reportStatuses: this.reportStatuses
    };

    const dialogRef = this.dialog.open(ChangeReportStatusDialogComponent, dialogConfig);
    dialogRef.afterClosed().subscribe(updated => {
      if (updated === true) {
        this.getReports();
      }
    });
  }

  private downloadReport(report: Report): void {
    this.overlayService.show();

    this.reportDesignerService.exportReport(report.id)
      .pipe(finalize(() => this.overlayService.hide()))
      .subscribe({
        next: response => {
          const blob = new Blob([response.body], { type: 'application/json' });
          const fileName = report.name.replace(/[\\/:*?"<>|]/g, '-');
          fileSaver.saveAs(blob, `${fileName}.hrreport.json`);
        },
        error: () => {
          this.snackbarService.openSnackBar('Unable to download report', 'clear', 'warn');
        }
      });
  }

  onImportFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];
    input.value = '';

    if (!file) {
      return;
    }

    this.overlayService.show();

    this.reportDesignerService.importReport(file)
      .pipe(finalize(() => this.overlayService.hide()))
      .subscribe({
        next: () => {
          this.snackbarService.openSnackBar('Report imported successfully', 'clear', 'success');
          this.getReports();
        },
        error: () => {
          this.snackbarService.openSnackBar('Unable to import report - the file may not be a valid report bundle for this system', 'clear', 'warn');
        }
      });
  }
}
